import React, { useCallback, useMemo, useState } from 'react';
import { Box, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { srcLight as SearchNoResultsIllustration } from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/empty/components/search-no-results';
import { useIntl } from '@atlassian/jira-intl';
import { AsyncMappingTable } from '../../../common/ui/mapping-table';
import { useMappingState } from '../../../controllers/import-state';
import { Filters } from './filters';
import { ALL, type Filter } from './filters/constants';
import messages from './messages';
import { useTableHead, useTableRows } from './utils';

const ROWS_PER_PAGE = 8;

type Props = {
	isLoading: boolean;
};

export const FieldMapping = ({ isLoading }: Props) => {
	const [mappingState, { setMappingStateValidationError }] = useMappingState();
	const [selectedFilter, setFilter] = useState<Filter | undefined>(ALL);
	const [searchTerm, setSearchTerm] = useState('');
	const [isErrorToggleEnabled, setIsErrorToggleEnabled] = useState(false);
	const { formatMessage } = useIntl();
	const tableHead = useTableHead();
	const tableRows = useTableRows({
		mappingState,
		searchTerm,
		filter: selectedFilter,
		isErrorToggleEnabled,
	});

	const onErrorColumnsToggle = useCallback(
		(isChecked: boolean) => {
			if (isChecked && mappingState !== null && mappingState.validationError !== null) {
				setMappingStateValidationError(mappingState.validationError);
			}
			setIsErrorToggleEnabled(isChecked);
		},
		[mappingState, setMappingStateValidationError],
	);

	const emptyView = useMemo(
		() => (
			<Stack alignInline="center" alignBlock="center" grow="fill">
				<Box as="img" src={SearchNoResultsIllustration} alt="" xcss={illustrationStyles} />
				<Box as="p" xcss={emptyDescriptionStyles}>
					{formatMessage(messages.emptyViewMessage)}
				</Box>
			</Stack>
		),
		[formatMessage],
	);

	return (
		<Box testId="project-create-importer-upload-flow.ui.mapping-screen.field-mapping.box">
			<Filters
				selectedFilter={selectedFilter || ALL}
				onSearch={setSearchTerm}
				onFilterChange={setFilter}
				onToggleChange={onErrorColumnsToggle}
				showErrorToggle={!!mappingState && mappingState.validationError !== null}
			/>
			<AsyncMappingTable
				head={tableHead}
				rows={tableRows}
				rowsPerPage={ROWS_PER_PAGE}
				isLoading={isLoading}
				emptyView={emptyView}
			/>
		</Box>
	);
};

const illustrationStyles = xcss({
	maxHeight: '120px',
	marginBlockEnd: 'space.300',
});

const emptyDescriptionStyles = xcss({
	font: token('font.heading.medium'),
	color: 'color.text',
});
